import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Zap, CheckCircle2, XCircle, FileText } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

/**
 * "HAfiscal Admin -> Podešavanja -> Email (SMTP)" - vidi razgovor "dodaj polje
 * za smtp nalog za sada, posle mozemo sendgrid/mailgun". Isti UI pattern kao
 * ServiceEndpointsPage.tsx (maskirana lozinka, dugme "Pošalji test mejl").
 * NAMERNO odvojeno od CompanySettingsPage SMTP-a (to je po-firmi, za slanje
 * faktura kupcima) - ovo je PLATFORMSKI SMTP za sistemske mailove (potvrda
 * naloga, nalog potvrđen, reset lozinke).
 */
export function EmailSettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ["platform-mail-settings"],
    queryFn: () => api.getPlatformMailSettings(),
  });

  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("HAFiskal");
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [testTo, setTestTo] = useState("");

  if (settings && !hydrated) {
    setSmtpHost(settings.smtpHost ?? "");
    setSmtpPort(settings.smtpPort ?? 587);
    setSmtpUser(settings.smtpUser ?? "");
    setSmtpFrom(settings.smtpFrom ?? "");
    setSmtpFromName(settings.smtpFromName ?? "HAFiskal");
    setSmtpSecure(settings.smtpSecure);
    setHydrated(true);
  }

  const save = useMutation({
    mutationFn: () =>
      api.updatePlatformMailSettings({
        smtpHost,
        smtpPort,
        smtpUser,
        smtpFrom,
        smtpFromName,
        smtpSecure,
        ...(smtpPass ? { smtpPass } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-mail-settings"] });
      setSmtpPass("");
    },
  });

  const test = useMutation({
    mutationFn: () => api.sendTestMail(testTo),
  });

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Podešavanja / Email (SMTP)</h1>
          <p className="text-sm text-slate-500">
            Platformski SMTP nalog za sistemske mailove (potvrda naloga, nalog potvrđen, reset lozinke).
          </p>
        </div>
        <Link to="/finora-admin/email-templates">
          <Button variant="secondary">
            <FileText className="h-4 w-4" aria-hidden />
            Email Template-i
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <div className="grid gap-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="mb-1 block text-xs text-slate-500">SMTP Host</label>
                <input
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="npr. smtp.hafiskal.rs"
                  className="h-9 w-full rounded-lg border border-border px-2 font-mono text-xs focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Port</label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(Number(e.target.value))}
                  className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">SMTP korisničko ime</label>
                <input
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">
                  SMTP lozinka {settings?.hasSmtpConfig && "(trenutno podešena)"}
                </label>
                <input
                  type="password"
                  value={smtpPass}
                  onChange={(e) => setSmtpPass(e.target.value)}
                  placeholder={settings?.hasSmtpConfig ? "ostavi prazno da ne menjaš" : ""}
                  className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Mail "od" adresa</label>
                <input
                  type="email"
                  value={smtpFrom}
                  onChange={(e) => setSmtpFrom(e.target.value)}
                  placeholder="npr. podrska@hafiskal.rs"
                  className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Prikazano ime pošiljaoca</label>
                <input
                  value={smtpFromName}
                  onChange={(e) => setSmtpFromName(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={smtpSecure}
                onChange={(e) => setSmtpSecure(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Koristi SSL/TLS (obično uključeno za port 465)
            </label>
          </div>

          {save.isError && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
            <div className="text-xs text-slate-400">
              {settings?.updatedAt && `Poslednja izmena: ${new Date(settings.updatedAt).toLocaleString("sr-RS")}`}
            </div>
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Čuvam..." : "Sačuvaj"}
            </Button>
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="tvoj@email.com"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <Button size="sm" variant="secondary" disabled={test.isPending || !testTo} onClick={() => test.mutate()}>
              <Zap className="h-3.5 w-3.5" aria-hidden />
              Pošalji test mejl
            </Button>
          </div>
          {test.isSuccess && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
              Test mejl je poslat.
            </div>
          )}
          {test.isError && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              <XCircle className="h-4 w-4 shrink-0" aria-hidden />
              {test.error instanceof ApiError ? test.error.response.message : "Slanje test mejla nije uspelo."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
